/**
 * KineticHeadline — display headline whose lines rise out of a mask one after
 * another (web, CSS only). Once they've landed, the letters answer the
 * pointer: the one under it springs up and turns green, its neighbors follow
 * halfway (fine pointers only). `outline` lines are drawn as hollow strokes
 * that fill in where the pointer passes. Screen readers get the whole
 * sentence once, via the heading's label. Native and reduced motion: static
 * lines (outlines stay hollow, which is still legible).
 *
 *   <KineticHeadline lines={['Pay for the edge,', 'not the noise.']} outline={[1]} textStyle={styles.title} />
 *   <KineticHeadline lines={['Find your edge.']} enter={false} level={2} />  // no entrance (a Reveal handles it), h2
 */
import React, { useEffect, useState } from 'react';
import { View, Text } from 'react-native';
import { IS_WEB, useReducedMotion } from '../../utils/motion';
import injectCss from '../../utils/injectCss';

const CSS = `
[data-kinetic-line] { display: block; animation: boKineticUp 1000ms cubic-bezier(0.16,1,0.3,1) both; animation-delay: 120ms; }
[data-kinetic-line="1"] { animation-delay: 230ms; }
[data-kinetic-line="2"] { animation-delay: 340ms; }
[data-kinetic-line="3"] { animation-delay: 450ms; }
@keyframes boKineticUp { from { transform: translate3d(0, 108%, 0) rotate(2deg); } to { transform: none; } }
[data-hl-word] { display: inline-block; white-space: nowrap; }
[data-hl] { display: inline-block; position: relative; transition: transform 700ms cubic-bezier(0.34,1.56,0.64,1), color 420ms cubic-bezier(0.2,0,0,1), -webkit-text-stroke-color 420ms; }
/* Extends each letter's hover area below it so a lifted letter doesn't flicker. */
[data-hl]::after { content: ''; position: absolute; left: 0; right: 0; top: 100%; height: 22%; }
[data-kinetic-outline] [data-hl] { color: transparent; -webkit-text-stroke: 1.5px rgba(255,255,255,0.78); }
@media (hover: hover) and (pointer: fine) {
  [data-hl]:hover { transform: translate3d(0, -12%, 0) rotate(-4deg); color: #34D399; transition-duration: 220ms, 160ms, 160ms; }
  [data-hl]:hover + [data-hl], [data-hl]:has(+ [data-hl]:hover) { transform: translate3d(0, -5%, 0); color: #A5B4FC; transition-duration: 260ms, 200ms, 200ms; }
  [data-kinetic-outline] [data-hl]:hover { color: #34D399; -webkit-text-stroke-color: #34D399; }
  [data-kinetic-outline] [data-hl]:hover + [data-hl], [data-kinetic-outline] [data-hl]:has(+ [data-hl]:hover) { color: rgba(165,180,252,0.35); -webkit-text-stroke-color: #A5B4FC; }
}
@media (prefers-reduced-motion: reduce) { [data-kinetic-line] { animation: none; } [data-hl] { transition: none; transform: none !important; } }
`;

const SETTLE_MS = 1600; // after the last line lands, drop the mask so lifted letters aren't clipped

function Letters({ line }) {
    const words = line.split(' ');
    return words.map((word, w) => (
        <React.Fragment key={`${w}-${word}`}>
            {w > 0 && ' '}
            <Text dataSet={{ hlWord: '' }}>
                {Array.from(word).map((ch, i) => <Text key={`${i}-${ch}`} dataSet={{ hl: '' }}>{ch}</Text>)}
            </Text>
        </React.Fragment>
    ));
}

export default function KineticHeadline({ lines = [], textStyle, style, align = 'center', outline = [], interactive = true, enter = true, level }) {
    const reduced = useReducedMotion();
    const animate = IS_WEB && !reduced && enter;
    const letters = IS_WEB && interactive;
    const [settled, setSettled] = useState(!animate);
    if (IS_WEB) injectCss('kinetic', CSS);

    useEffect(() => {
        if (!animate) { setSettled(true); return undefined; }
        const t = setTimeout(() => setSettled(true), SETTLE_MS);
        return () => clearTimeout(t);
    }, [animate]);

    return (
        <View style={[{ alignItems: align === 'center' ? 'center' : 'flex-start' }, style]} accessible accessibilityRole="header" accessibilityLabel={lines.join(' ')} {...(level ? { 'aria-level': level } : {})}>
            {lines.map((line, i) => {
                const hollow = outline.includes(i);
                const data = {
                    ...(animate ? { kineticLine: String(Math.min(i, 3)) } : {}),
                    ...(IS_WEB && hollow ? { kineticOutline: '' } : {}),
                };
                return (
                    // The mask: overflow hidden with a little bottom room for descenders.
                    <View key={`${i}-${line}`} style={{ overflow: settled ? 'visible' : 'hidden', paddingBottom: 4, marginBottom: -4 }} importantForAccessibility="no-hide-descendants" accessibilityElementsHidden aria-hidden>
                        <Text
                            style={[textStyle, { textAlign: align }, !IS_WEB && hollow && { opacity: 0.6 }]}
                            {...(Object.keys(data).length ? { dataSet: data } : {})}
                        >
                            {letters ? <Letters line={line} /> : line}
                        </Text>
                    </View>
                );
            })}
        </View>
    );
}
